import { useState, useEffect } from 'react';
import {
  createTypesenseClient,
  getCollectionNames,
  getTypesenseSearchConfigError,
  TYPESENSE_SEARCH_CONFIG_ERROR,
} from '../utils/typesense';

const PER_PAGE = 40;

/**
 * Global brewery name search for autocomplete (Typesense, no state filter).
 *
 * @param {string} searchQuery - Typing input; debounce at call site
 * @returns {{ options: Array<{id: string, name: string, city?: string, state?: string}>, loading: boolean, error: string|null }}
 */
export function useBreweryAutocompleteSearch(searchQuery) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const trimmed = typeof searchQuery === 'string' ? searchQuery.trim() : '';

    async function run() {
      if (!trimmed) {
        setOptions([]);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      const configError = getTypesenseSearchConfigError();
      if (configError) {
        if (!cancelled) {
          setOptions([]);
          setError(configError);
          setLoading(false);
        }
        return;
      }

      const mode = (import.meta.env.MODE || 'development').toLowerCase();
      const env = ['development', 'staging', 'production'].includes(mode)
        ? mode
        : 'development';
      const { breweries: collectionName } = getCollectionNames(env);

      if (!collectionName) {
        if (!cancelled) {
          setOptions([]);
          setError(TYPESENSE_SEARCH_CONFIG_ERROR);
          setLoading(false);
        }
        return;
      }

      const client = createTypesenseClient();
      if (!client) {
        if (!cancelled) {
          setOptions([]);
          setError(TYPESENSE_SEARCH_CONFIG_ERROR);
          setLoading(false);
        }
        return;
      }

      try {
        const searchResponse = await client
          .collections(collectionName)
          .documents()
          .search({
            q: trimmed,
            query_by: 'name',
            sort_by: 'name:asc',
            per_page: PER_PAGE,
            page: 1,
          });

        if (cancelled) return;

        const hits = searchResponse?.hits ?? [];
        const rows = hits.map((hit) => {
          const doc = hit.document ?? hit;
          return {
            id: doc.id ?? hit.id,
            name: doc.name ?? '',
            city: doc.city,
            state: doc.state,
          };
        });
        setOptions(rows);
      } catch (fetchError) {
        if (cancelled) return;
        console.error('Brewery autocomplete search failed:', fetchError);
        setOptions([]);
        setError(
          fetchError?.message || 'Failed to search breweries. Please try again.',
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [searchQuery]);

  return { options, loading, error };
}
